import React, { useState, useRef, useCallback } from 'react';
import { Segment } from 'tea-component';
import { getLocale } from 'umi';
import SegmentAutoSlider from './SegmentAutoSlider';
import { slideHorizontalTo } from './slide';
import { LANGUAGE_CN } from '@intelligence/_config/config';

export default function useAnchor(config: any) {
  const {
    options,
    data,
    getParentNode,
    getSlideNode,
    onChange,
    onClick = () => {},
    minColumnWidth = 0,
  } = config || {};
  const initialValue = Array.isArray(options) && options.length ? options[0].value : '';
  const [value, setValue] = useState(initialValue);
  const scrollingRef = useRef(false);

  const calculateSlideDistance = (segmentValue: string): number => {
    let distance = 0;
    try {
      // 点击第一个 segment（例如 Steam）时，直接回到最左侧
      const firstSegValue = Array.isArray(options) && options.length ? options[0].value : '';
      if (segmentValue === firstSegValue) {
        return 0;
      }

      const locale = getLocale();
      const isCN = locale === LANGUAGE_CN;
      
      // 找到对应的 segment 配置
      const segmentItem = options?.find((item: any) => item?.value === segmentValue);
      if (!segmentItem) {
        return 0;
      }
      
      // 使用 stepValue 查找实际的列
      const targetValue = segmentItem?.stepValue || segmentValue;
      const distanceOffset = segmentItem?.distanceOffset || 0;
      
      const index = data.findIndex((item: any) => item.value === targetValue);
      
      if (index !== -1) {
        for (let j = 0; j < index; j++) {
          const item = data[j];
          // 根据语言选择对应的宽度字段，并与实际表格列宽的最小值保持一致
          const width = isCN ? (item?.width_cn || item?.width) : item?.width;
          const widthNumber = Number(width);
          const normalizedWidth = Number.isFinite(widthNumber) ? widthNumber : minColumnWidth;

          distance += Math.max(normalizedWidth, minColumnWidth);
        }
        
        // 加上偏移量
        distance += distanceOffset;
      }
    } catch (e) {
      console.error('[useSegmentAnchor] error:', e);
      distance = 0;
    }
    return distance;
  };

  const doAnchor = useCallback((segValue: string) => {
    // 简单的防抖
    if (scrollingRef.current) return;
    
    const item = options?.find((t: any) => t?.value === segValue);
    if (!(getSlideNode instanceof Function) || !item) return;
    
    scrollingRef.current = true;
    
    // 延迟执行滚动，避免阻塞主线程
    setTimeout(() => {
      try {
        const slideNode = getSlideNode();
        const distance = calculateSlideDistance(segValue);
        
        if (Array.isArray(slideNode)) {
          slideNode.forEach((node: Element) => {
            if (node) slideHorizontalTo(node, distance);
          });
        } else {
          if (slideNode) slideHorizontalTo(slideNode, distance);
        }
      } finally {
        // 300ms后允许下次滚动
        setTimeout(() => {
          scrollingRef.current = false;
        }, 300);
      }
    }, 0);
  }, [options, getSlideNode, calculateSlideDistance]);

  const handleSegmentChange = (newValue: string) => {
    onClick?.(newValue);
    doAnchor(newValue);
    if (newValue === value) return;
    setValue(newValue);
    if (onChange instanceof Function) {
      onChange(newValue);
    }
  };

  const updateAnchorValue = (value: string) => {
    if (!Array.isArray(options)) return;
    if (!options.map((item) => item.value).includes(value)) return;
    doAnchor(value);
    setValue(value);
  };

  const updateAnchor = (newValue?: string) => {
    const _newValue = newValue || value;
    doAnchor(_newValue);
  };

  const renderAnchorSegmentComp = (defaultValue?: string) => {
    return React.createElement(
      React.Fragment,
      {},
      React.createElement(
        SegmentAutoSlider,
        { getParentNode },
        React.createElement(Segment, {
          className: 'segment-light',
          value: defaultValue,
          options: Array.isArray(options) ? options : [],
          onChange: handleSegmentChange,
        }),
      ),
    );
  };

  return {
    renderAnchorSegmentComp,
    updateAnchorValue,
    updateAnchor,
  };
}
